import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { InputText } from 'primereact/inputtext';
import MlxButton from '../../../../../../components/common/MlxButton/MlxButton';
import { JTranslation } from '../../../../../../helpers/jTranslationHelper';
import { useTranslation } from '../../../../../../hooks/useTranslation';
import {
    _ImportFileError,
    _MenuNameForm,
    initialMenuNameForm,
    MENU_IMPORT_ACCEPT,
    MENU_IMPORT_MAX_IMAGES,
    menuNameSchema,
    validateImportFiles,
} from '../../../../../../helpers/menuImportHelper';
import {
    MENU_IMPORT_CLEAR_ALL,
    MENU_IMPORT_DROP_FILES,
    MENU_IMPORT_EXTRACT_MENU,
    MENU_IMPORT_FILE_ERROR_EMPTY,
    MENU_IMPORT_FILE_ERROR_IMAGES,
    MENU_IMPORT_FILE_ERROR_MIXED,
    MENU_IMPORT_FILE_ERROR_NAME,
    MENU_IMPORT_FILE_ERROR_PDFS,
    MENU_IMPORT_FILE_ERROR_SIZE,
    MENU_IMPORT_FILE_ERROR_TYPE,
    MENU_IMPORT_FILES_HEADING,
    MENU_IMPORT_FORMAT_HELP,
    MENU_IMPORT_MENU_NAME,
    MENU_IMPORT_MENU_NAME_PLACEHOLDER,
    MENU_IMPORT_MENU_NAME_REQUIRED,
    MENU_IMPORT_MENU_NAME_TOO_LONG,
    MENU_IMPORT_NO_FILES,
    MENU_IMPORT_NOTHING_ADDED,
    MENU_IMPORT_OR_BROWSE,
    MENU_IMPORT_REMOVE_FILE,
    MENU_IMPORT_UPLOAD_HINT_MISSING,
} from '../../../../../../constants/strings';
import SelectedFileCard from './SelectedFileCard';

const languageTexts = {
    FILES_HEADING: MENU_IMPORT_FILES_HEADING,
    MENU_NAME_PLACEHOLDER: MENU_IMPORT_MENU_NAME_PLACEHOLDER,
    REMOVE_FILE: MENU_IMPORT_REMOVE_FILE,
};

const FILE_ERROR_TEXT: Record<_ImportFileError, string> = {
    nameTooLong: MENU_IMPORT_FILE_ERROR_NAME,
    mixedTypes: MENU_IMPORT_FILE_ERROR_MIXED,
    tooManyPdfs: MENU_IMPORT_FILE_ERROR_PDFS,
    tooManyImages: MENU_IMPORT_FILE_ERROR_IMAGES,
    unsupportedType: MENU_IMPORT_FILE_ERROR_TYPE,
    tooLarge: MENU_IMPORT_FILE_ERROR_SIZE,
    empty: MENU_IMPORT_FILE_ERROR_EMPTY,
};

// A stable key per File object, so removing a file does not remount (and re-thumbnail) the cards after it.
const fileKeys = new WeakMap<File, number>();
let nextFileKey = 0;
const getFileKey = (file: File) => {
    if (!fileKeys.has(file)) fileKeys.set(file, nextFileKey++);
    return fileKeys.get(file);
};

type _UploadStepProps = {
    files: File[];
    onFilesChange: (files: File[]) => void;
    initialMenuName: string;
    onExtract: (menuName: string) => void;
    /** True when the organization has no imports left: Extract is disabled. */
    quotaExhausted?: boolean;
};

const UploadStep = ({ files, onFilesChange, initialMenuName, onExtract, quotaExhausted }: Readonly<_UploadStepProps>) => {
    const translatedText = useTranslation(languageTexts, 'none');
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const formik = useFormik<_MenuNameForm>({
        initialValues: { ...initialMenuNameForm, menuName: initialMenuName },
        validationSchema: menuNameSchema,
        onSubmit: (values) => {
            if (!quotaExhausted) onExtract(values.menuName.trim());
        },
    });

    const { error: fileError } = validateImportFiles(files);
    const isNameValid = menuNameSchema.isValidSync(formik.values);
    const nameError = formik.touched.menuName && formik.errors.menuName;
    const inputMissing = files.length === 0 || !isNameValid;

    const addFiles = (picked: File[]) => {
        if (picked.length) onFilesChange([...files, ...picked]);
    };

    const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(event.target.files ?? []));
        event.target.value = '';
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        addFiles(Array.from(event.dataTransfer.files));
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    return (
        <form className="bulk-upload-card bulk-upload-step-card" onSubmit={formik.handleSubmit} noValidate>
            <div className="bulk-upload-upload-grid" data-testid="bulk-upload-grid">
                <div
                    className={`bulk-upload-dropzone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'compact' : ''}`}
                    data-testid="bulk-upload-dropzone"
                    onDragOver={handleDragOver}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                >
                    <i className="ri-upload-cloud-2-line bulk-upload-dropzone-icon" />
                    <span>
                        <JTranslation text={MENU_IMPORT_DROP_FILES} />
                    </span>
                    <button type="button" className="bulk-upload-browse" onClick={() => inputRef.current?.click()}>
                        <JTranslation text={MENU_IMPORT_OR_BROWSE} />
                    </button>
                    <span className="bulk-upload-muted">
                        <JTranslation text={MENU_IMPORT_FORMAT_HELP} />
                    </span>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        hidden
                        accept={MENU_IMPORT_ACCEPT}
                        data-testid="bulk-upload-input"
                        onChange={handlePick}
                    />
                </div>

                <div className="bulk-upload-upload-side" data-testid="bulk-upload-side">
                    <div className="bulk-upload-files-heading">
                        <span className="bulk-upload-files-title" data-testid="bulk-upload-files-heading">
                            {(translatedText.FILES_HEADING ?? MENU_IMPORT_FILES_HEADING)
                                .replace('{count}', String(files.length))
                                .replace('{max}', String(MENU_IMPORT_MAX_IMAGES))}
                        </span>
                        {files.length > 0 && (
                            <button type="button" className="bulk-upload-clear-all" onClick={() => onFilesChange([])}>
                                <JTranslation text={MENU_IMPORT_CLEAR_ALL} />
                            </button>
                        )}
                    </div>
                    <div className="bulk-upload-files-scroll" data-testid="bulk-upload-files-scroll">
                        {files.length === 0 ? (
                            <span className="bulk-upload-muted bulk-upload-files-empty" data-testid="bulk-upload-files-empty">
                                <JTranslation text={MENU_IMPORT_NO_FILES} />
                            </span>
                        ) : (
                            <ul className="bulk-upload-files">
                                {files.map((file, index) => (
                                    <SelectedFileCard
                                        key={getFileKey(file)}
                                        file={file}
                                        removeLabel={`${translatedText.REMOVE_FILE ?? MENU_IMPORT_REMOVE_FILE} ${file.name}`}
                                        onRemove={() => onFilesChange(files.filter((_, i) => i !== index))}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>

                    {fileError && (
                        <span className="bulk-upload-error-text" role="alert">
                            <JTranslation text={FILE_ERROR_TEXT[fileError]} />
                        </span>
                    )}

                    <div className="bulk-upload-field">
                        <label htmlFor="menuName">
                            <JTranslation text={MENU_IMPORT_MENU_NAME} />
                        </label>
                        <InputText
                            id="menuName"
                            name="menuName"
                            value={formik.values.menuName}
                            placeholder={translatedText.MENU_NAME_PLACEHOLDER}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                    </div>

                    {nameError && (
                        <span className="bulk-upload-error-text" role="alert">
                            <JTranslation
                                text={formik.values.menuName.trim() ? MENU_IMPORT_MENU_NAME_TOO_LONG : MENU_IMPORT_MENU_NAME_REQUIRED}
                            />
                        </span>
                    )}
                </div>
            </div>
            <div className="bulk-upload-footer-bar" data-testid="bulk-upload-footer">
                <span className="bulk-upload-muted" data-testid="bulk-upload-hint">
                    <JTranslation text={inputMissing ? MENU_IMPORT_UPLOAD_HINT_MISSING : MENU_IMPORT_NOTHING_ADDED} />
                </span>
                <MlxButton type="submit" disabled={inputMissing || !!fileError || !!quotaExhausted}>
                    <JTranslation text={MENU_IMPORT_EXTRACT_MENU} />
                </MlxButton>
            </div>
        </form>
    );
};

export default UploadStep;
